import { Component, inject, OnInit, OnDestroy, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { AdminUser, Permiso } from '../models/api.models';
import { PermissionsService } from '../services/permissions.service';
import { AlertsService } from '../services/alerts.service';
import { isSuperAdmin, canManageUsers, hasPermission, canManageComplaints } from '../helpers/permissions.helper';


// Dashboard statistics interface
interface DashboardStats {
    title: string;
    value: string | number;
    icon: string;
    color: string;
}

// Menu item interface
interface MenuItem {
    id: string;
    label: string;
    icon: string;
}

// User form interface
interface UserFormData {
    nombre: string;
    usuario: string;
    contrasena?: string;
    permissionIds?: number[];  // Permission IDs for the user
}

// Room form interface
interface RoomFormData {
    numero: string;
    nombre?: string;
    tipo: string;
    tamano?: string;
    precio: number;
    capacidad: number;
    descripcion?: string;
    piso?: number;
    imagen?: string;
    imageFile?: File;
}

// Guest form interface
interface GuestFormData {
    nombre: string;
    apellido: string;
    tipoDocumento: string;
    numeroDocumento: string;
    telefono: string;
    email?: string;
    nacionalidad: string;
    fechaNacimiento?: string;
    direccion?: string;
    observaciones?: string;
}

interface BookingFormData {
    roomId: number;
    guestId: number;
    checkInDate: string;
    checkOutDate: string;
    numberOfAdults: number;
    numberOfChildren: number;
    notes?: string;
}

import { TopBarComponent } from '../components/top-bar/top-bar.component';
import { LoaderComponent } from '../components/loader/loader.component';

@Component({
    selector: 'app-dashboard',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterOutlet, TopBarComponent, LoaderComponent],
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
    private readonly authService = inject(AuthService);
    private readonly permissionsService = inject(PermissionsService);
    private readonly alertsService = inject(AlertsService);
    private readonly router = inject(Router);

    // Initial loading state
    isLoading = signal(true);

    // User info
    userName = signal('Administrador');
    currentDate = signal('');
    currentUser = signal<AdminUser | null>(null);

    // Current section
    currentSection = signal('welcome');

    // Permission checks for module visibility
    canViewUsers = computed(() => {
        const user = this.currentUser();
        return user ? hasPermission(user, 'get_users') : false;
    });

    canManageUsers = computed(() => {
        const user = this.currentUser();
        return user ? hasPermission(user, 'manage_users') : false;
    });

    canManageRooms = computed(() => {
        const user = this.currentUser();
        return user ? hasPermission(user, 'manage_rooms') : false;
    });

    canManageGuests = computed(() => {
        const user = this.currentUser();
        return user ? hasPermission(user, 'manage_guests') : false;
    });

    canManageBookings = computed(() => {
        const user = this.currentUser();
        return user ? hasPermission(user, 'manage_bookings') : false;
    });

    canManageInventory = computed(() => {
        const user = this.currentUser();
        return user ? hasPermission(user, 'manage_inventory') : false;
    });

    canManageComplaints = computed(() => {
        const user = this.currentUser();
        return user ? canManageComplaints(user) : false;
    });


    // Users Management State - Removed (Lazy Loaded)
    // Rooms Management State - Removed (Lazy Loaded)

    // Guests Management State - Removed (Lazy Loaded)
    // Bookings Management State - Removed (Lazy Loaded)

    goToNewBooking(): void {
        this.router.navigate(['/admin/bookings/new']);
    }


    // Menu items - filtered by permissions
    menuItems = computed(() => {
        const user = this.currentUser();
        console.log('ðŸ” menuItems computed - Current user:', user);
        console.log('ðŸ” User permissions:', user?.permisos);
        console.log('ðŸ” canViewUsers:', this.canViewUsers());
        console.log('ðŸ” canManageUsers:', this.canManageUsers());
        console.log('ðŸ” canManageRooms:', this.canManageRooms());
        console.log('ðŸ” canManageGuests:', this.canManageGuests());
        console.log('ðŸ” canManageBookings:', this.canManageBookings());

        const items: MenuItem[] = [
            {
                id: 'dashboard',
                label: 'Dashboard',
                icon: 'M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z'
            }
        ];

        // Add Reservaciones if user has permission
        if (this.canManageBookings()) {
            console.log('✅ Adding Reservaciones');
            items.push({
                id: 'reservaciones',
                label: 'Reservaciones',
                icon: 'M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z'
            });
            // Add Limpieza (same permission as bookings)
            items.push({
                id: 'limpieza',
                label: 'Limpieza',
                icon: 'M21.9 10.3c0-.2-.1-.3-.2-.4h-1.3v-1.1c0-.6-.4-1-1-1s-1 .4-1 1v1.1h-3.8V5.6c0-.6-.4-1-1-1s-1 .4-1 1v4.3h-1.3c-.2 0-.3.1-.4.2-.1.1-.2.3-.1.5.7 2.2 1.8 3.4 3.5 3.8v8.7c0 .5.4.9.9.9h.8c.5 0 .9-.4.9-.9v-8.7c1.7-.4 2.8-1.6 3.5-3.8.1-.2 0-.4-.1-.5z'
            });
        }

        // Add Habitaciones if user has permission
        if (this.canManageRooms()) {
            console.log('✅ Adding Habitaciones');
            items.push({
                id: 'habitaciones',
                label: 'Habitaciones',
                icon: 'M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4v2h8v-2h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H4V4h16v12z'
            });
        }

        // Add Huéspedes if user has permission
        if (this.canManageGuests()) {
            console.log('✅ Adding Huéspedes');
            items.push({
                id: 'huespedes',
                label: 'Huéspedes',
                icon: 'M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'
            });
        }

        // Add Inventario if user has permission
        if (this.canManageInventory()) {
            console.log('✅ Adding Inventario');
            items.push({
                id: 'inventario',
                label: 'Inventario',
                icon: 'M20 2H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h3l-1 1v2h12v-2l-1-1h3c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 13H4V4h16v11z'
            });
        }

        // Add Ventas if user has permission (same as inventory)
        if (this.canManageInventory()) {
            console.log('✅ Adding Ventas');
            items.push({
                id: 'ventas',
                label: 'Ventas',
                icon: 'M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z'
            });
        }

        // Add Facturación if user has permission
        if (this.canManageBookings()) {
            console.log('✅ Adding Facturación');
            items.push({
                id: 'facturacion',
                label: 'Facturación',
                icon: 'M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z'
            });
        }

        // Add Reclamos if user has permission
        if (this.canManageComplaints()) {
            console.log('✅ Adding Reclamos');
            items.push({
                id: 'reclamos',
                label: 'Reclamos',
                icon: 'M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm7 16H5V5h2v3h10V5h2v14z'
            });
        }

        // Add Usuarios if user has permission
        if (this.canViewUsers()) {
            console.log('✅ Adding Usuarios');
            items.push({
                id: 'usuarios',
                label: 'Usuarios',
                icon: 'M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z'
            });
        }


        console.log('📋 Final menu items:', items);
        return items;
    });


    // Dashboard data (simplified for welcome page)
    stats = signal<DashboardStats[]>([
        {
            title: 'Habitaciones Ocupadas',
            value: '45/60',
            icon: 'bed',
            color: 'primary'
        },
        {
            title: 'Check-ins Hoy',
            value: 8,
            icon: 'calendar',
            color: 'secondary'
        },
        {
            title: 'Huéspedes Activos',
            value: 92,
            icon: 'users',
            color: 'success'
        }
    ]);

    ngOnInit(): void {
        // Set current date
        this.updateDate();

        // Load current user with permissions
        const user = this.authService.getCurrentUser();
        console.log('🔍 ngOnInit - getCurrentUser():', user);
        if (user) {
            this.currentUser.set(user);
            this.userName.set(user.nombre || 'Administrador');
            console.log('✅ User set in ngOnInit');
        } else {
            console.log('⚠️ No user found in ngOnInit');
        }

        // Subscribe to user changes
        this.authService.currentUser$.subscribe(user => {
            console.log('🔍 currentUser$ subscription triggered, user:', user);
            if (user) {
                this.currentUser.set(user);
                this.userName.set(user.nombre || 'Administrador');
                console.log('✅ User updated from subscription, permisos:', user.permisos);
            }
        });

        // Start alerts polling for notifications
        console.log('🔔 Starting alerts polling from DashboardComponent');
        this.alertsService.startPolling();

        // Subscribe to router events to update currentSection
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
            this.updateCurrentSectionFromUrl(event.url);
        });

        // Set initial section based on current URL
        this.updateCurrentSectionFromUrl(this.router.url);

        // Simulated initial data loading delay for better UX
        setTimeout(() => {
            console.log('✨ Initial loading finished');
            this.isLoading.set(false);
        }, 2000);
    }

    ngOnDestroy(): void {
        // Stop alerts polling when dashboard is destroyed
        console.log('🔔 Stopping alerts polling from DashboardComponent');
        this.alertsService.stopPolling();
    }

    /**
     * Update currentSection based on URL
     */
    private updateCurrentSectionFromUrl(url: string): void {
        if (url.includes('/inventario')) {
            this.currentSection.set('inventario');
        } else if (url.includes('/ventas')) {
            this.currentSection.set('ventas');
        } else if (url.includes('/usuario')) {
            this.currentSection.set('usuarios');
        } else if (url.includes('/habitacion')) {
            this.currentSection.set('habitaciones');
        } else if (url.includes('/huesped')) {
            this.currentSection.set('huespedes');
        } else if (url.includes('/reservacion')) {
            this.currentSection.set('reservaciones');
        } else if (url.includes('/limpieza')) {
            this.currentSection.set('limpieza');
        } else if (url.includes('/facturacion')) {
            this.currentSection.set('facturacion');
        } else if (url.includes('/complaints')) {
            this.currentSection.set('reclamos');
        } else if (url === '/admin/dashboard' || url === '/admin/dashboard/') {
            this.currentSection.set('dashboard');
        } else {
            this.currentSection.set('welcome');
        }
    }

    /**
     * Update current date
     */
    private updateDate(): void {
        const now = new Date();
        const options: Intl.DateTimeFormatOptions = {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        };
        this.currentDate.set(now.toLocaleDateString('es-ES', options));
    }

    /**
     * Navigate to section
     */
    navigateToSection(sectionId: string): void {
        // Handle dashboard navigation via router
        if (sectionId === 'dashboard') {
            this.router.navigate(['/admin/dashboard']);
            return;
        }

        // Handle inventory navigation via router
        if (sectionId === 'inventario') {
            this.router.navigate(['/admin/dashboard/inventario']);
            return;
        }

        // Handle sales navigation via router
        if (sectionId === 'ventas') {
            this.router.navigate(['/admin/dashboard/ventas']);
            return;
        }

        // Handle users navigation via router
        if (sectionId === 'usuarios') {
            this.router.navigate(['/admin/dashboard/usuario']);
            return;
        }

        // Handle rooms navigation via router
        if (sectionId === 'habitaciones') {
            this.router.navigate(['/admin/dashboard/habitacion']);
            return;
        }

        // Handle guests navigation via router
        if (sectionId === 'huespedes') {
            this.router.navigate(['/admin/dashboard/huesped']);
            return;
        }

        // Handle bookings navigation via router
        if (sectionId === 'reservaciones') {
            this.router.navigate(['/admin/dashboard/reservacion']);
            return;
        }

        // Handle limpieza navigation via router
        if (sectionId === 'limpieza') {
            this.router.navigate(['/admin/dashboard/limpieza']);
            return;
        }

        // Handle facturación navigation via router
        if (sectionId === 'facturacion') {
            this.router.navigate(['/admin/dashboard/facturacion']);
            return;
        }

        // Handle reclamos navigation via router
        if (sectionId === 'reclamos') {
            this.router.navigate(['/admin/dashboard/complaints']);
            return;
        }

        // For other sections, use inline switching
        this.currentSection.set(sectionId);
    }

    /**
     * Get section title
     */
    getSectionTitle(sectionId: string): string {
        const titles: { [key: string]: string } = {
            'reservaciones': 'Reservaciones',
            'limpieza': 'Limpieza',
            'habitaciones': 'Habitaciones',
            'huespedes': 'Huéspedes',
            'usuarios': 'Usuarios',
            'inventario': 'Inventario',
            'ventas': 'Ventas',
            'facturacion': 'Facturación',
            'reclamos': 'Reclamos',
            'reportes': 'Reportes',
            'configuracion': 'Configuración'
        };
        return titles[sectionId] || 'Dashboard';
    }

    /**
     * Logout user
     */
    logout(): void {
        this.authService.logout();
        this.router.navigate(['/admin/login']);
    }

    /**
     * Get icon SVG path based on icon name
     */
    getIconPath(iconName: string): string {
        const icons: { [key: string]: string } = {
            'bed': 'M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4v2h8v-2h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H4V4h16v12z',
            'calendar': 'M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z',
            'users': 'M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z'
        };
        return icons[iconName] || icons['calendar'];
    }

    /**
     * Navigate to welcome screen
     */
    navigateToWelcome(): void {
        this.router.navigate(['/admin/dashboard/welcome']);
    }
}
